import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Bookmark, Loader2, MoreVertical, Plus } from "lucide-react";

import AlertSettingsModal from "./AlertSettingsModal";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import { Header } from "../../../components/Header/Header";
import {
  createSavedSearch,
  deleteSavedSearch,
  listSavedSearches,
  markSavedSearchViewed,
} from "../../../services/savedSearchesApi";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";
import { EMPTY_FILTERS, filtersToUrl } from "./TenderFilters";

// A handful of real, useful starting points — not everyone's first saved
// search should have to start from a blank filter set. No result count is
// shown on these: it isn't fetched, and a guessed number is worse than none.
const STARTER_SEARCHES = [
  {
    name: "Set-aside tenders closing in 7 days",
    state: {
      keywords: "",
      ordering: "closing_soon",
      filters: { ...EMPTY_FILTERS, set_aside_only: true, closing_within_days: 7, require_deadline: true },
    },
  },
  {
    name: "IT & Software posted this week",
    state: {
      keywords: "",
      ordering: "recently_posted",
      filters: { ...EMPTY_FILTERS, category: ["it-software"], posted_since_days: 7 },
    },
  },
  {
    name: "Sources sought — anywhere, any industry",
    state: {
      keywords: "",
      ordering: "closing_soon",
      filters: { ...EMPTY_FILTERS, notice_type: ["sources_sought"] },
    },
  },
];

const alertLabel = (row) => {
  if (!row.alert_email || row.alert_frequency === "off") return "Off";
  return row.alert_frequency === "weekly" ? "Weekly email" : "Daily email";
};

// Coarse and forgiving on purpose — this is "roughly when," not a log.
const relativeTime = (iso) => {
  if (!iso) return "Never";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short" });
};

function RowMenu({ onOpen, onEditAlerts, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="More actions"
        className="p-1.5 rounded-md text-[#71717A] hover:text-[#18101C] hover:bg-gray-100"
      >
        <MoreVertical className="w-[18px] h-[18px]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 w-36 bg-white border border-[#E5E7EB]
                          rounded-lg shadow-lg py-1">
            <button
              type="button"
              onClick={() => { setOpen(false); onOpen(); }}
              className="w-full text-left px-3 py-2 text-[13px] font-medium text-[#18101C] hover:bg-gray-50"
            >
              Open
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); onEditAlerts(); }}
              className="w-full text-left px-3 py-2 text-[13px] font-medium text-[#18101C] hover:bg-gray-50"
            >
              Alerts
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(); }}
              className="w-full text-left px-3 py-2 text-[13px] font-medium text-[#DC2626] hover:bg-gray-50"
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function SavedSearchesPage() {
  const navigate = useNavigate();
  const { isMenuOpen } = useOutletContext() || {};

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertsTarget, setAlertsTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [savingStarter, setSavingStarter] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listSavedSearches();
      setRows(data);
    } catch {
      showErrorToast("Could not load your saved searches.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totalNew = useMemo(() => rows.reduce((sum, r) => sum + (r.new_count || 0), 0), [rows]);

  const openSavedSearch = useCallback(
    (row) => {
      markSavedSearchViewed(row.id).catch(() => {});
      const state = row.state || { keywords: "", ordering: "closing_soon", filters: EMPTY_FILTERS };
      const qs = filtersToUrl(state.filters || EMPTY_FILTERS, {
        keywords: state.keywords || "",
        ordering: state.ordering || "closing_soon",
      });
      navigate(`/tender-search?${qs.toString()}`);
    },
    [navigate]
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSavedSearch(deleteTarget.id);
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      showSuccessToast(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
    } catch {
      showErrorToast("Could not delete this saved search. Please try again.");
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  const saveStarter = useCallback(async (starter) => {
    setSavingStarter(starter.name);
    try {
      const row = await createSavedSearch({
        name: starter.name,
        state: starter.state,
        alertEmail: false,
        alertFrequency: "off",
      });
      setRows((prev) => [row, ...prev]);
      showSuccessToast(`Saved "${row.name}"`);
    } catch {
      showErrorToast("Could not save this search. Please try again.");
    } finally {
      setSavingStarter(null);
    }
  }, []);

  return (
    <div className="flex flex-col h-full w-full font-outfit overflow-hidden bg-[#FAFAFA]">
      <div className="flex-shrink-0 bg-white">
        <Header name="Saved Searches" icon="" isMenuOpen={isMenuOpen} onClick={() => {}} />
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar px-6 pt-5 pb-8">
        <div className="max-w-[900px] mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#9CA3AF]">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center max-w-[430px] mx-auto">
              <span className="w-[46px] h-[46px] rounded-full bg-[#F9F2F8] grid place-items-center mb-3.5">
                <Bookmark className="w-[22px] h-[22px] text-[#BB42AE]" strokeWidth={1.9} />
              </span>
              <h3 className="text-[17px] font-semibold text-[#18101C]">Nothing saved yet</h3>
              <p className="mt-1.5 text-[13.5px] leading-[1.6] text-[#52525B]">
                A saved search keeps a set of filters and a keyword. Open it and it re-runs against
                today's feed; turn on alerts and we email you the new matches instead.
              </p>
              <button
                type="button"
                onClick={() => navigate("/tender-search")}
                className="mt-4 font-outfit text-[14px] font-semibold text-white bg-[#A04A97]
                           hover:bg-[#8f4188] rounded-md px-4 py-2"
              >
                Go to Tender Search
              </button>

              <div className="mt-7 border-t border-[#E5E7EB] pt-4 w-full text-left">
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9CA3AF] mb-2">
                  Or start from one of these
                </p>
                <div className="flex flex-col gap-1.5">
                  {STARTER_SEARCHES.map((s) => (
                    <div
                      key={s.name}
                      className="flex items-center gap-2.5 border border-[#E5E7EB] bg-white rounded-lg px-3 py-2.5"
                    >
                      <span className="flex-1 text-[13.5px] font-medium text-[#18101C]">{s.name}</span>
                      <button
                        type="button"
                        disabled={savingStarter === s.name}
                        onClick={() => saveStarter(s)}
                        className="text-[12.5px] font-semibold text-[#BB42AE] hover:underline disabled:opacity-60 shrink-0"
                      >
                        {savingStarter === s.name ? "Saving..." : "Save"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <p className="flex-1 text-[14px] text-[#71717A]">
                  <b className="text-[#18101C] font-semibold">{rows.length}</b> saved search
                  {rows.length === 1 ? "" : "es"}
                  {totalNew > 0 && (
                    <>
                      {" "}· <b className="text-[#18101C] font-semibold">{totalNew}</b> new tender
                      {totalNew === 1 ? "" : "s"} across them
                    </>
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/tender-search")}
                  className="inline-flex items-center gap-1.5 font-outfit text-[13.5px] font-medium
                             text-[#A04A97] border border-[#A04A97] rounded-md px-3 py-1.5 bg-white
                             hover:bg-[#FDF6FC]"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New search
                </button>
              </div>

              <div className="bg-white border border-[#E5E7EB] rounded-xl">
                {rows.map((row, i) => (
                  <div
                    key={row.id}
                    onClick={() => openSavedSearch(row)}
                    className={`flex items-center gap-4 px-4 py-3.5 cursor-pointer hover:bg-[#FAFAFA] ${
                      i > 0 ? "border-t border-[#F1F1F3]" : ""
                    } ${i === 0 ? "rounded-t-xl" : ""} ${i === rows.length - 1 ? "rounded-b-xl" : ""}`}
                  >
                    <Bookmark className="w-[15px] h-[15px] text-[#BB42AE] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[14.5px] font-semibold text-[#18101C] truncate">{row.name}</p>
                      <p className="mt-0.5 text-[12px] text-[#9CA3AF]">
                        Alerts: {alertLabel(row)} · Last opened {relativeTime(row.last_viewed_at)}
                      </p>
                    </div>
                    {row.new_count > 0 && (
                      <span className="font-outfit text-[12px] font-bold text-white bg-[#BB42AE]
                                       rounded-full px-2.5 py-0.5 shrink-0">
                        {row.new_count} new
                      </span>
                    )}
                    <div onClick={(e) => e.stopPropagation()}>
                      <RowMenu
                        onOpen={() => openSavedSearch(row)}
                        onEditAlerts={() => setAlertsTarget(row)}
                        onDelete={() => setDeleteTarget(row)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-start gap-2.5 border border-[#E5E7EB] bg-white rounded-lg px-4 py-3">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"
                     className="shrink-0 mt-[1px]">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <p className="text-[13px] leading-[1.5] text-[#52525B]">
                  Saved searches are yours alone — nobody else in your organization can see them.
                  Alert emails go to your account address.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <AlertSettingsModal
        open={alertsTarget != null}
        savedSearch={alertsTarget}
        onClose={() => setAlertsTarget(null)}
        onSaved={(updated) =>
          setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
        }
      />

      {deleteTarget && (
        <ConfirmDialog
          title="Delete saved search"
          message={`"${deleteTarget.name}" and its alert settings will be deleted. This can't be undone.`}
          confirmLabel="Delete"
          danger
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
